import { useState, type ReactNode } from 'react';
import { Badge, Box, Button, Card, Group, Loader, Stack, Text, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { staffPath, useAssignStall, useSnoozeStall, useStalls, type Stall } from '@/api/ops';
import { useRefreshRun } from '@/api/runs';
import { useAuth } from '@/auth/useAuth';
import { EmptyState } from '@/components/EmptyState';
import { useFormat } from '@/components/useFormat';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { RunErrorLine, type FailedRunLike } from '@/features/runs/RunErrorLine';
import { runFailure } from '@/features/runs/runError';
import { brand } from '@/theme/theme';
import { SnoozeStallModal } from './SnoozeStallModal';

/** Fixed order by severity (UX review §1.2): what is broken first, what is merely untidy last. */
export const KIND_ORDER = [
  'run_stalled',
  'run_unsubmitted',
  'analysis_failed',
  'object_waiting',
  'session_inactive',
  'analysis_ready',
  'analysis_review',
  'object_bytes_missing',
];

function rank(kind: string): number {
  const i = KIND_ORDER.indexOf(kind);
  return i === -1 ? KIND_ORDER.length : i;
}

/** Stalls grouped by kind in severity order; within a group the API's order (oldest first). */
export function groupByKind(stalls: Stall[]): [string, Stall[]][] {
  const groups = new Map<string, Stall[]>();
  for (const s of stalls) groups.set(s.kind, [...(groups.get(s.kind) ?? []), s]);
  return [...groups.entries()].sort(([a], [b]) => rank(a) - rank(b));
}

function groupByProject(stalls: Stall[]): [Stall['project'], Stall[]][] {
  const groups = new Map<string, [Stall['project'], Stall[]]>();
  for (const s of stalls) {
    const entry = groups.get(s.project.public_id) ?? [s.project, []];
    entry[1].push(s);
    groups.set(s.project.public_id, entry);
  }
  return [...groups.values()].sort((a, b) => b[1].length - a[1].length);
}

function useStallName() {
  const { i18n } = useTranslation();
  return (s: Stall) => (s.subject_name ? (i18n.language === 'en' ? s.subject_name.en : s.subject_name.cs) : s.subject_id);
}

/** The one action that moves a row forward, by its kind; a link where the work needs its page. */
function PrimaryAction({ stall }: { stall: Stall }) {
  const { t } = useTranslation('ops');
  const refresh = useRefreshRun();
  const qc = useQueryClient();
  const to = stall.link ? staffPath(stall.link) : undefined;
  const linkAction = (label: string, hint?: string) =>
    to ? (
      <Button component={Link} to={to} size="compact-xs" variant="light" title={hint}>
        {label}
      </Button>
    ) : null;

  switch (stall.kind) {
    case 'run_stalled':
    case 'run_unsubmitted':
      return (
        <Button
          size="compact-xs"
          variant="light"
          loading={refresh.isPending}
          title={t('stalls.action.refreshHint')}
          onClick={() =>
            refresh.mutate(stall.subject_id, {
              onSuccess: (r) => {
                notifications.show({ message: t(r.advanced ? 'stalls.action.refreshed' : 'stalls.action.refreshedNothing') });
                void qc.invalidateQueries({ queryKey: ['stalls'] });
              },
              onError: (err) => notifications.show({ color: 'red', message: errorMessage(err) }),
            })
          }
        >
          {t('stalls.action.refresh')}
        </Button>
      );
    case 'analysis_failed':
      return linkAction(t('stalls.action.rerun'), t('stalls.action.rerunHint'));
    case 'analysis_review':
      return linkAction(t('stalls.action.deliver'), t('stalls.action.deliverHint'));
    case 'object_waiting':
      return linkAction(t(stall.detail.preprocess_available ? 'stalls.action.preprocess' : 'stalls.action.open'));
    default:
      return linkAction(t('stalls.action.open'));
  }
}

/**
 * The row's run when forun's failure explains it (or it failed without a reason), for
 * `RunErrorLine`; the row carries its own forun link, so the line does not repeat it.
 */
export function failedRun(stall: Stall): FailedRunLike | undefined {
  if (!stall.run) return undefined;
  const failure = runFailure(stall.run_error);
  if (stall.run_state !== 'failed' && !(failure && (failure.code || failure.message))) return undefined;
  return { public_id: stall.run, state: stall.run_state, error: stall.run_error, forun_url: null };
}

/** How long a row has waited: under an hour, whole hours below two days, then whole days. */
export function useStallAge() {
  const { t } = useTranslation('ops');
  const { number } = useFormat();
  return (hours: number) => {
    if (hours < 1) return t('stalls.ageUnderHour');
    if (hours < 48) return t('stalls.ageHours', { hours: number(Math.floor(hours)) });
    return t('stalls.ageDays', { count: Math.floor(hours / 24) });
  };
}

function Row({ stall, onSnooze }: { stall: Stall; onSnooze: (s: Stall) => void }) {
  const { t } = useTranslation('ops');
  const { dateTime } = useFormat();
  const age = useStallAge();
  const { me } = useAuth();
  const name = useStallName();
  const assign = useAssignStall();
  const snooze = useSnoozeStall();
  const mine = !!me && stall.assignee?.public_id === me.public_id;
  const kindLabel = useKindLabel();
  const label = name(stall);
  const dataKind = stall.kind === 'object_waiting' && typeof stall.detail.kind === 'string' ? stall.detail.kind : null;
  const run = failedRun(stall);
  const onError = (err: unknown) => notifications.show({ color: 'red', message: errorMessage(err) });

  return (
    <Group justify="space-between" px="md" py={8} wrap="nowrap" align="flex-start" style={{ borderTop: `1px solid ${brand.border}` }}>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Group gap="sm" wrap="wrap">
          {stall.link ? (
            <Link to={staffPath(stall.link)} style={{ color: brand.text, fontWeight: 600, textDecoration: 'none' }}>
              <Text fz={13} fw={600}>
                {label}
              </Text>
            </Link>
          ) : (
            <Text fz={13} fw={600}>
              {label}
            </Text>
          )}
          {label !== stall.subject_id && (
            <Text ff="monospace" fz={11} c={brand.textMuted}>
              {stall.subject_id}
            </Text>
          )}
          {dataKind && (
            <Text fz={12} c={brand.textMuted} title={t('stalls.dataKind')}>
              {kindLabel(dataKind)}
            </Text>
          )}
          <Text fz={13} fw={600} c={brand.textMuted}>
            {stall.project.name}
          </Text>
          {stall.assignee && (
            <Badge size="xs" variant="light" color={mine ? 'forest' : 'gray'}>
              {t('stalls.assignee', { name: stall.assignee.full_name || stall.assignee.email })}
            </Badge>
          )}
        </Group>
        <Group gap="xs" wrap="wrap">
          {stall.author && (
            <Text fz={12} c={brand.textMuted}>
              {t('stalls.author', { name: stall.author.full_name || stall.author.email })}
            </Text>
          )}
          {run ? (
            <RunErrorLine run={run} lines={2} maw={720} />
          ) : (
            stall.error && (
              <Text fz={12} c="red" lineClamp={2} title={stall.error} style={{ overflowWrap: 'anywhere' }}>
                {stall.error}
              </Text>
            )
          )}
          {stall.forun_url && (
            <Text component="a" href={stall.forun_url} target="_blank" rel="noopener" fz={12} c={brand.textMuted}>
              {t('stalls.forun')} ↗
            </Text>
          )}
          {stall.snoozed_until && (
            <Text fz={12} c={brand.textMuted}>
              {t('stalls.snoozedUntil', { when: dateTime(stall.snoozed_until) })}
              {stall.snooze_note ? ` · ${stall.snooze_note}` : ''}
            </Text>
          )}
        </Group>
      </Stack>
      <Group gap={6} wrap="nowrap">
        <Text fz={13} c={brand.textMuted} title={t('stalls.ageHint', { when: dateTime(stall.since) })} style={{ whiteSpace: 'nowrap' }}>
          {age(stall.age_h)}
        </Text>
        <PrimaryAction stall={stall} />
        <Button
          size="compact-xs"
          variant="subtle"
          loading={assign.isPending}
          title={mine ? t('stalls.releaseHint') : t('stalls.takeHint')}
          onClick={() => assign.mutate({ kind: stall.kind, subject_id: stall.subject_id, take: !mine }, { onError })}
        >
          {mine ? t('stalls.release') : t('stalls.take')}
        </Button>
        {stall.snoozed_until ? (
          <Button
            size="compact-xs"
            variant="subtle"
            loading={snooze.isPending}
            title={t('stalls.wakeHint')}
            onClick={() => snooze.mutate({ kind: stall.kind, subject_id: stall.subject_id, until: null, note: '' }, { onError })}
          >
            {t('stalls.wake')}
          </Button>
        ) : (
          <Button size="compact-xs" variant="subtle" title={t('stalls.snooze.buttonHint')} onClick={() => onSnooze(stall)}>
            {t('stalls.snooze.button')}
          </Button>
        )}
      </Group>
    </Group>
  );
}

/** Folded noise: one line per project, its objects as links. */
function ProjectRows({ stalls }: { stalls: Stall[] }) {
  const { t } = useTranslation('ops');
  const name = useStallName();
  return (
    <>
      {groupByProject(stalls).map(([project, rows]) => (
        <Box key={project.public_id} px="md" py={8} style={{ borderTop: `1px solid ${brand.border}` }}>
          <Group gap="sm">
            <Link to={staffPath(`/staff/projects/${project.public_id}`)} style={{ color: brand.text, textDecoration: 'none' }}>
              <Text fz={13} fw={600}>
                {project.name}
              </Text>
            </Link>
            <Text fz={12} c={brand.textMuted}>
              {t('stalls.objectsCount', { count: rows.length })}
            </Text>
          </Group>
          <Text fz={12} c={brand.textMuted}>
            {rows.map((s, i) => (
              <span key={s.subject_id}>
                {i > 0 && ', '}
                {s.link ? (
                  <Link to={staffPath(s.link)} style={{ color: brand.textMuted }}>
                    {name(s)}
                  </Link>
                ) : (
                  name(s)
                )}
              </span>
            ))}
          </Text>
        </Box>
      ))}
    </>
  );
}

/** A collapsible card per group; its header toggles it. */
function StallGroup({ title, count, defaultOpen, children }: { title: string; count: number; defaultOpen: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card p={0}>
      <UnstyledButton onClick={() => setOpen((o) => !o)} aria-expanded={open} px="md" py={8} bg={brand.surface2} style={{ display: 'block', width: '100%' }}>
        <Group justify="space-between">
          <Text fz={13} fw={700}>
            {open ? '▾' : '▸'} {title}
          </Text>
          <Text fz={12} c={brand.textMuted}>
            {count}
          </Text>
        </Group>
      </UnstyledButton>
      {open && children}
    </Card>
  );
}

/** The staff home board: everything stuck, by severity, with who took it and what to do next. */
export function StallsScreen() {
  const { t } = useTranslation('ops');
  const stalls = useStalls();
  const [snoozing, setSnoozing] = useState<Stall | null>(null);

  if (stalls.isPending) return <Loader color="forest" />;
  if (stalls.isError) return <EmptyState title={errorMessage(stalls.error)} />;
  if (stalls.data.length === 0) return <EmptyState title={t('stalls.empty')} />;

  const open = stalls.data.filter((s) => !s.snoozed_until);
  const snoozed = stalls.data.filter((s) => s.snoozed_until);
  const kindTitle = (kind: string) => t(`stalls.kind.${kind}`, { defaultValue: kind });

  return (
    <Stack gap="md">
      {open.length === 0 && <EmptyState title={t('stalls.allSnoozed')} />}
      {groupByKind(open).map(([kind, rows]) => {
        const noise = rows.every((s) => s.noise);
        return (
          <StallGroup key={kind} title={kindTitle(kind)} count={rows.length} defaultOpen={!noise}>
            {noise ? <ProjectRows stalls={rows} /> : rows.map((s) => <Row key={`${s.kind}:${s.subject_id}`} stall={s} onSnooze={setSnoozing} />)}
          </StallGroup>
        );
      })}
      {snoozed.length > 0 && (
        <StallGroup title={t('stalls.snoozedGroup')} count={snoozed.length} defaultOpen={false}>
          {snoozed.map((s) => (
            <Row key={`${s.kind}:${s.subject_id}`} stall={s} onSnooze={setSnoozing} />
          ))}
        </StallGroup>
      )}
      <SnoozeStallModal stall={snoozing} onClose={() => setSnoozing(null)} />
    </Stack>
  );
}
